import { FormEvent, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  createPaymentSettings,
  fetchPaymentSettings,
  updatePaymentSettings,
  type PaymentSettings,
  type VatTypeCode,
} from "../../api/paymentSettings";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import { GlassDateTimePicker } from "../GlassDateTimePicker/GlassDateTimePicker";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { Shimmer } from "../Shimmer/Shimmer";
import { useNotification } from "../Notification/NotificationProvider";
import "../RequestsSection/RequestsSection.scss";
import "../CreateRequestModal/CreateRequestModal.scss";
import "./PaymentSettingsSection.scss";

type PaymentMode = "bank" | "manual";

type FormState = {
  mode: PaymentMode;
  apiToken: string;
  tokenExpiresAt: string;
  customerCode: string;
  merchantId: string;
  taxation: VatTypeCode;
  successRedirectUrl: string;
  failRedirectUrl: string;
  clientId: string;
  paymentServerUrl: string;
  webhookUrl: string;
};

const MODE_OPTIONS: { value: PaymentMode; label: string }[] = [
  { value: "bank", label: "Оплата через банк" },
  { value: "manual", label: "Ручное подтверждение" },
];

const VAT_TYPE_OPTIONS: { value: VatTypeCode; label: string }[] = [
  { value: "0", label: "0" },
  { value: "5", label: "5" },
  { value: "7", label: "7" },
  { value: "10", label: "10" },
  { value: "22", label: "22" },
  { value: "105", label: "105" },
  { value: "107", label: "107" },
  { value: "110", label: "110" },
  { value: "122", label: "122" },
];

const VAT_TYPE_VALUES = new Set(VAT_TYPE_OPTIONS.map((option) => option.value));

const normalizeVatType = (value: unknown): VatTypeCode | "" => {
  if (value == null || value === "") return "";

  if (typeof value === "number" && Number.isFinite(value)) {
    const code = String(value) as VatTypeCode;
    return VAT_TYPE_VALUES.has(code) ? code : "";
  }

  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === "none") return "";
    const withoutPrefix = trimmed.startsWith("vat") ? trimmed.slice(3) : trimmed;
    if (VAT_TYPE_VALUES.has(withoutPrefix as VatTypeCode)) {
      return withoutPrefix as VatTypeCode;
    }
  }

  return "";
};

const emptyForm = (): FormState => ({
  mode: "manual",
  apiToken: "",
  tokenExpiresAt: "",
  customerCode: "",
  merchantId: "",
  taxation: "22",
  successRedirectUrl: "",
  failRedirectUrl: "",
  clientId: "",
  paymentServerUrl: "",
  webhookUrl: "",
});

const toLocalInput = (iso?: string): string => {
  if (!iso?.trim()) return "";
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalInput = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (!Number.isFinite(date.getTime())) return undefined;
  return date.toISOString();
};

const settingsToForm = (settings: PaymentSettings | null): FormState => {
  if (!settings) return emptyForm();
  return {
    mode: settings.bankPaymentsEnabled ? "bank" : "manual",
    apiToken: settings.apiToken ?? "",
    tokenExpiresAt: toLocalInput(settings.tokenExpiresAt),
    customerCode: settings.customerCode ?? "",
    merchantId: settings.merchantId ?? "",
    taxation: normalizeVatType(settings.taxation) || "22",
    successRedirectUrl: settings.successRedirectUrl ?? "",
    failRedirectUrl: settings.failRedirectUrl ?? "",
    clientId: settings.clientId ?? "",
    paymentServerUrl: settings.paymentServerUrl ?? "",
    webhookUrl: settings.webhookUrl ?? "",
  };
};

const formToPayload = (
  form: FormState,
  existing: PaymentSettings | null
): PaymentSettings => {
  return {
    ...existing,
    id: existing?.id,
    bankPaymentsEnabled: form.mode === "bank",
    apiToken: form.apiToken.trim() || undefined,
    tokenExpiresAt: fromLocalInput(form.tokenExpiresAt),
    customerCode: form.customerCode.trim() || undefined,
    merchantId: form.merchantId.trim() || undefined,
    taxation: Number(form.taxation),
    successRedirectUrl: form.successRedirectUrl.trim() || undefined,
    failRedirectUrl: form.failRedirectUrl.trim() || undefined,
    clientId: form.clientId.trim() || undefined,
    paymentServerUrl: form.paymentServerUrl.trim() || undefined,
    webhookUrl: form.webhookUrl.trim() || undefined,
  };
};

export const PaymentSettingsSection = () => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<PaymentSettings | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [showToken, setShowToken] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchPaymentSettings()
      .then((settings) => {
        setExisting(settings);
        setForm(settingsToForm(settings));
        setShowToken(false);
      })
      .catch((err: unknown) => {
        setError(getApiErrorMessage(err, "Не удалось загрузить настройки платежей"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    const payload = formToPayload(form, existing);

    try {
      const saved = existing != null
        ? await updatePaymentSettings(payload)
        : await createPaymentSettings(payload);
      setExisting(saved);
      setForm(settingsToForm(saved));
      setShowToken(false);
      showNotification({
        message: "Настройки платежей сохранены",
        variant: "success",
      });
    } catch (err: unknown) {
      showNotification({
        message: getApiErrorMessage(err, "Не удалось сохранить настройки платежей"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const maskedToken = form.apiToken ? "*".repeat(form.apiToken.length) : "";
  const bankFieldsDisabled = form.mode === "manual";

  return (
    <section
      className="requests-section requests-section--embedded payment-settings-section"
      aria-label="Платежи"
    >
      <div className="requests-section-header">
        <h1 className="requests-section-title">Платежи</h1>
      </div>

      {loading ? (
        <Shimmer rows={6} />
      ) : error ? (
        <div className="requests-section-error">
          <p className="requests-section-error__text">{error}</p>
          <button type="button" className="requests-section-error__retry" onClick={load}>
            Повторить
          </button>
        </div>
      ) : (
        <form className="payment-settings-form create-request-modal-form" onSubmit={handleSubmit}>
          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-mode">
              Способ оплаты
            </label>
            <FormDropdown
              value={form.mode}
              options={MODE_OPTIONS}
              onChange={(mode) => setForm((current) => ({ ...current, mode }))}
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-api-token">
              Токен
            </label>
            <div className="payment-settings-token">
              <input
                id="payment-api-token"
                className="create-request-modal-input"
                type="text"
                autoComplete="off"
                spellCheck={false}
                disabled={bankFieldsDisabled}
                value={showToken ? form.apiToken : maskedToken}
                onChange={(event) => {
                  if (!showToken || bankFieldsDisabled) return;
                  setForm((current) => ({ ...current, apiToken: event.target.value }));
                }}
                onFocus={() => {
                  if (bankFieldsDisabled) return;
                  if (!showToken) setShowToken(true);
                }}
                readOnly={!showToken || bankFieldsDisabled}
              />
              <button
                type="button"
                className="payment-settings-token-toggle"
                aria-label={showToken ? "Скрыть токен" : "Показать токен"}
                disabled={bankFieldsDisabled}
                onClick={() => setShowToken((current) => !current)}
              >
                {showToken ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-token-expires">
              Срок действия токена
            </label>
            <GlassDateTimePicker
              id="payment-token-expires"
              value={form.tokenExpiresAt}
              disabled={bankFieldsDisabled}
              onChange={(tokenExpiresAt) =>
                setForm((current) => ({ ...current, tokenExpiresAt }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-customer-code">
              Код продавца (customerCode)
            </label>
            <input
              id="payment-customer-code"
              className="create-request-modal-input"
              type="text"
              autoComplete="off"
              disabled={bankFieldsDisabled}
              value={form.customerCode}
              onChange={(event) =>
                setForm((current) => ({ ...current, customerCode: event.target.value }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-merchant-id">
              Код торговой точки (merchantId)
            </label>
            <input
              id="payment-merchant-id"
              className="create-request-modal-input"
              type="text"
              autoComplete="off"
              disabled={bankFieldsDisabled}
              value={form.merchantId}
              onChange={(event) =>
                setForm((current) => ({ ...current, merchantId: event.target.value }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-taxation">
              Система налогообложения
            </label>
            <FormDropdown
              value={form.taxation}
              options={VAT_TYPE_OPTIONS}
              disabled={bankFieldsDisabled}
              onChange={(taxation) => setForm((current) => ({ ...current, taxation }))}
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-success-url">
              Redirect URL после успешного платежа
            </label>
            <input
              id="payment-success-url"
              className="create-request-modal-input"
              type="url"
              autoComplete="off"
              placeholder="https://"
              disabled={bankFieldsDisabled}
              value={form.successRedirectUrl}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  successRedirectUrl: event.target.value,
                }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-fail-url">
              Redirect URL после неуспешного платежа
            </label>
            <input
              id="payment-fail-url"
              className="create-request-modal-input"
              type="url"
              autoComplete="off"
              placeholder="https://"
              disabled={bankFieldsDisabled}
              value={form.failRedirectUrl}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  failRedirectUrl: event.target.value,
                }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-client-id">
              Client ID
            </label>
            <input
              id="payment-client-id"
              className="create-request-modal-input"
              type="text"
              autoComplete="off"
              disabled={bankFieldsDisabled}
              value={form.clientId}
              onChange={(event) =>
                setForm((current) => ({ ...current, clientId: event.target.value }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-server-url">
              Payment Server URL
            </label>
            <input
              id="payment-server-url"
              className="create-request-modal-input"
              type="url"
              autoComplete="off"
              placeholder="https://"
              disabled={bankFieldsDisabled}
              value={form.paymentServerUrl}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  paymentServerUrl: event.target.value,
                }))
              }
            />
          </div>

          <div className="create-request-modal-form-group">
            <label className="create-request-modal-label" htmlFor="payment-webhook-url">
              Webhook URL
            </label>
            <input
              id="payment-webhook-url"
              className="create-request-modal-input"
              type="url"
              autoComplete="off"
              placeholder="https://"
              disabled={bankFieldsDisabled}
              value={form.webhookUrl}
              onChange={(event) =>
                setForm((current) => ({ ...current, webhookUrl: event.target.value }))
              }
            />
          </div>

          <div className="payment-settings-actions">
            <button
              type="submit"
              className="create-request-modal-submit"
              disabled={saving}
            >
              {saving ? <CircularProgress size="sm" /> : "Сохранить"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};
